/**
 * Agent-Level Layout Component
 *
 * Wraps agent-level pages (Playground, Activity, Analytics, etc.)
 * when a user clicks into a specific chatbot/agent.
 * Provides chatbot data via AgentContext to all child routes.
 */

import React, { createContext, useContext, useEffect, useState } from 'react'
import { Outlet, useParams, Link } from 'react-router-dom'
import { Loader2, Copy, Check } from 'lucide-react'
import { useChatbotStore } from '../../stores/chatbotStore'
import { AgentSidebar } from './AgentSidebar'
import { SidebarProvider, SidebarInset, SidebarTrigger } from '../ui/sidebar'
import { Separator } from '../ui/Separator'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb'
import { ThemeToggle } from '../ui/ThemeToggle'
import { NotificationCenter } from '../notifications/NotificationCenter'
import type { Chatbot } from '../../types'

// --- Agent Context ---

interface AgentContextValue {
  chatbot: Chatbot
  refetch: () => Promise<void>
}

export const AgentContext = createContext<AgentContextValue | null>(null)

export const useAgentContext = () => {
  const ctx = useContext(AgentContext)
  if (!ctx) throw new Error('useAgentContext must be used within AgentContextLayout')
  return ctx
}

// --- Layout Component ---

export const AgentContextLayout: React.FC = () => {
  const { chatbotId } = useParams<{ chatbotId: string }>()
  const { selectedChatbot, fetchChatbot, isLoading, error } = useChatbotStore()

  useEffect(() => {
    if (chatbotId) {
      fetchChatbot(chatbotId)
    }
  }, [chatbotId, fetchChatbot])

  const refetch = async () => {
    if (chatbotId) {
      await fetchChatbot(chatbotId)
    }
  }

  // Loading state
  if (isLoading && !selectedChatbot) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Error state
  if (error && !selectedChatbot) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center gap-4">
        <p className="text-sm text-destructive">Failed to load agent: {error}</p>
        <Link
          to="/dashboard/chatbots"
          className="text-sm text-primary underline-offset-4 hover:underline"
        >
          Back to Agents
        </Link>
      </div>
    )
  }

  // Chatbot not found
  if (!selectedChatbot || selectedChatbot.id !== chatbotId) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const CopyBotId: React.FC<{ id: string }> = ({ id }) => {
    const [copied, setCopied] = useState(false)
    return (
      <button
        onClick={() => {
          navigator.clipboard.writeText(id)
          setCopied(true)
          setTimeout(() => setCopied(false), 2000)
        }}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-mono text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted transition-colors"
        title="Copy Bot ID"
      >
        {copied ? (
          <Check className="w-3 h-3 text-emerald-500" />
        ) : (
          <Copy className="w-3 h-3" />
        )}
        <span className="hidden sm:inline">Agent ID: {id}</span>
        <span className="sm:hidden">Agent ID</span>
      </button>
    )
  }

  return (
    <AgentContext.Provider value={{ chatbot: selectedChatbot, refetch }}>
      <SidebarProvider>
        <AgentSidebar />
        <SidebarInset>
          {/* Simplified agent-level header */}
          <header className="sticky top-0 z-50 flex h-14 shrink-0 items-center gap-2 border-b bg-background/80 backdrop-blur-md px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />

            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/dashboard">CleverAct AI</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{selectedChatbot.name}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <div className="flex-1" />

            <div className="flex items-center gap-2">
              <CopyBotId id={selectedChatbot.id} />
              <Separator orientation="vertical" className="h-4" />
              <ThemeToggle />
              <NotificationCenter />
            </div>
          </header>

          <div className="flex-1 overflow-auto">
            <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
              <Outlet />
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </AgentContext.Provider>
  )
}
